import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const AssessmentCenter = () => {
  const [selectedAssessment, setSelectedAssessment] = useState(null);

  const assessments = [
    {
      id: 1,
      title: 'PNG Employment Act 1978 Knowledge Check',
      type: 'knowledge',
      questions: 25,
      duration: '30 min',
      passingScore: 80,
      attempts: 2,
      maxAttempts: 3,
      lastScore: 75,
      status: 'in-progress',
      description: 'Test your understanding of PNG Employment Act 1978 requirements, employee rights, and employer obligations.',
      topics: ['Minimum Wage', 'Leave Entitlements', 'Termination Procedures', 'Contract Requirements']
    },
    {
      id: 2,
      title: 'NASFUND Calculation Practical Exercise',
      type: 'practical',
      questions: 10,
      duration: '45 min',
      passingScore: 85,
      attempts: 0,
      maxAttempts: 3,
      lastScore: null,
      status: 'not-started',
      description: 'Demonstrate your ability to calculate NASFUND contributions accurately for various employee scenarios.',
      topics: ['Contribution Rates', 'Salary Calculations', 'Filing Procedures', 'Compliance Checks']
    },
    {
      id: 3,
      title: 'SWT Processing Certification Exam',
      type: 'certification',
      questions: 40,
      duration: '90 min',
      passingScore: 90,
      attempts: 1,
      maxAttempts: 2,
      lastScore: 88,
      status: 'in-progress',
      description: 'Comprehensive certification exam covering all aspects of Salary & Wages Tax processing for PNG.',
      topics: ['Tax Brackets', 'Deductions', 'Filing Requirements', 'Compliance Reporting']
    },
    {
      id: 4,
      title: 'Payroll Processing Competency Test',
      type: 'practical',
      questions: 15,
      duration: '60 min',
      passingScore: 85,
      attempts: 0,
      maxAttempts: 3,
      lastScore: null,
      status: 'locked',
      description: 'End-to-end payroll processing assessment including calculations, deductions, and government filings.',
      topics: ['Gross Pay', 'Statutory Deductions', 'Net Pay', 'Bank Files', 'Government Reporting'],
      prerequisite: 'Complete NASFUND and SWT assessments first'
    },
    {
      id: 5,
      title: 'Executive Dashboard Analytics Quiz',
      type: 'knowledge',
      questions: 20,
      duration: '25 min',
      passingScore: 80,
      attempts: 0,
      maxAttempts: 3,
      lastScore: null,
      status: 'not-started',
      description: 'Assess your understanding of executive dashboard metrics, compliance scoring, and strategic workforce analytics.',
      topics: ['KPI Interpretation', 'Compliance Metrics', 'Financial Projections', 'Department Performance']
    },
    {
      id: 6,
      title: 'Master Certification: PNG HR Compliance Expert',
      type: 'certification',
      questions: 50,
      duration: '120 min',
      passingScore: 95,
      attempts: 0,
      maxAttempts: 2,
      lastScore: null,
      status: 'locked',
      description: 'Ultimate certification exam covering all PNG HR compliance topics. Passing grants Expert Trainer status.',
      topics: ['All Topics'],
      prerequisite: 'Complete all other assessments with passing scores'
    }
  ];

  const getTypeColor = (type) => {
    switch (type) {
      case 'knowledge':
        return 'text-blue-600 bg-blue-50 border-blue-200';
      case 'practical':
        return 'text-purple-600 bg-purple-50 border-purple-200';
      case 'certification':
        return 'text-amber-600 bg-amber-50 border-amber-200';
      default:
        return 'text-muted-foreground bg-muted border-border';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'not-started':
        return 'text-muted-foreground bg-muted';
      case 'in-progress':
        return 'text-warning bg-warning/10';
      case 'passed':
        return 'text-success bg-success/10';
      case 'failed':
        return 'text-error bg-error/10';
      case 'locked':
        return 'text-muted-foreground bg-muted';
      default:
        return 'text-muted-foreground bg-muted';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'not-started':
        return 'Circle';
      case 'in-progress':
        return 'Clock';
      case 'passed':
        return 'CheckCircle';
      case 'failed':
        return 'XCircle';
      case 'locked':
        return 'Lock';
      default:
        return 'Circle';
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="bg-card border border-border rounded-lg p-6 mb-6">
        <div className="flex items-start gap-4">
          <div className="bg-amber-500/10 p-3 rounded-lg">
            <Icon name="Award" size={32} color="#f59e0b" />
          </div>
          <div className="flex-1">
            <h2 className="text-2xl font-heading font-bold text-foreground mb-2">
              Assessment Center
            </h2>
            <p className="text-muted-foreground mb-4">
              Test your knowledge and skills with comprehensive assessments. Passing scores are required for system access levels and certification.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-muted/50 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Icon name="Target" size={20} color="var(--color-primary)" />
                  <span className="text-sm font-semibold text-foreground">Passing Score</span>
                </div>
                <p className="text-2xl font-bold text-foreground">80-95%</p>
                <p className="text-xs text-muted-foreground">Varies by assessment</p>
              </div>
              <div className="bg-muted/50 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Icon name="RotateCcw" size={20} color="var(--color-warning)" />
                  <span className="text-sm font-semibold text-foreground">Attempts</span>
                </div>
                <p className="text-2xl font-bold text-foreground">2-3</p>
                <p className="text-xs text-muted-foreground">Maximum per assessment</p>
              </div>
              <div className="bg-muted/50 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Icon name="Award" size={20} color="#f59e0b" />
                  <span className="text-sm font-semibold text-foreground">Certifications</span>
                </div>
                <p className="text-2xl font-bold text-foreground">3</p>
                <p className="text-xs text-muted-foreground">Available to earn</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Assessments List */}
      <div className="space-y-4">
        {assessments?.map((assessment) => (
          <div
            key={assessment?.id}
            className={`bg-card border rounded-lg overflow-hidden ${
              assessment?.status === 'locked' ? 'border-muted opacity-60' : 'border-border hover:shadow-lg'
            } transition-all`}
          >
            <div className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-heading font-semibold text-foreground">
                      {assessment?.title}
                    </h3>
                    <span className={`px-3 py-1 rounded-full border text-xs font-semibold capitalize ${getTypeColor(assessment?.type)}`}>
                      {assessment?.type}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground mb-3">
                    {assessment?.description}
                  </p>
                  {assessment?.prerequisite && (
                    <div className="flex items-start gap-2 bg-warning/10 border border-warning/20 rounded-lg p-3 mb-3">
                      <Icon name="AlertTriangle" size={16} color="var(--color-warning)" className="mt-0.5" />
                      <p className="text-xs text-warning font-medium">{assessment?.prerequisite}</p>
                    </div>
                  )}
                </div>
                <div className={`px-3 py-1 rounded-full flex items-center gap-1.5 text-xs font-semibold ${getStatusColor(assessment?.status)}`}>
                  <Icon name={getStatusIcon(assessment?.status)} size={14} color="currentColor" />
                  {assessment?.status?.replace('-', ' ')?.toUpperCase()}
                </div>
              </div>

              {/* Topics */}
              <div className="mb-4">
                <p className="text-xs font-semibold text-foreground mb-2">Topics Covered:</p>
                <div className="flex flex-wrap gap-2">
                  {assessment?.topics?.map((topic, index) => (
                    <span key={index} className="px-2 py-1 bg-muted rounded text-xs text-muted-foreground">
                      {topic}
                    </span>
                  ))}
                </div>
              </div>

              {/* Metadata */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Questions</p>
                  <p className="text-sm font-semibold text-foreground">{assessment?.questions}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Duration</p>
                  <p className="text-sm font-semibold text-foreground">{assessment?.duration}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Passing Score</p>
                  <p className="text-sm font-semibold text-foreground">{assessment?.passingScore}%</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Attempts</p>
                  <p className="text-sm font-semibold text-foreground">{assessment?.attempts}/{assessment?.maxAttempts}</p>
                </div>
              </div>

              {/* Last Score */}
              {assessment?.lastScore !== null && (
                <div className="bg-muted/50 rounded-lg p-3 mb-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-foreground">Last Score:</span>
                    <span className={`text-lg font-bold ${
                      assessment?.lastScore >= assessment?.passingScore ? 'text-success' : 'text-error'
                    }`}>
                      {assessment?.lastScore}%
                    </span>
                  </div>
                  {assessment?.lastScore < assessment?.passingScore && (
                    <p className="text-xs text-muted-foreground mt-1">
                      Need {assessment?.passingScore - assessment?.lastScore}% more to pass
                    </p>
                  )}
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex items-center gap-3">
                {assessment?.status === 'locked' ? (
                  <Button fullWidth variant="outline" disabled iconName="Lock">
                    Locked
                  </Button>
                ) : assessment?.status === 'not-started' ? (
                  <Button fullWidth variant="default" iconName="Play">
                    Start Assessment
                  </Button>
                ) : assessment?.status === 'in-progress' ? (
                  <>
                    <Button fullWidth variant="default" iconName="Play">
                      Continue Assessment
                    </Button>
                    <Button variant="outline" iconName="RotateCcw">
                      Restart
                    </Button>
                  </>
                ) : (
                  <>
                    <Button fullWidth variant="outline" iconName="Eye">
                      View Results
                    </Button>
                    <Button variant="outline" iconName="RotateCcw">
                      Retake
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AssessmentCenter;